import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignupPage } from '@/features/auth/SignupPage';
import { BetsPage } from '@/features/bets/BetsPage';
import { EventPage } from '@/features/bets/EventPage';
import { BetHistoryPage } from '@/features/bets/BetHistoryPage';
import { SportsPage } from '@/features/bets/SportsPage';
import { SavingsPage } from '@/features/savings/SavingsPage';
import { DepositPage } from '@/features/deposits/DepositPage';
import { AccountPage } from '@/pages/AccountPage';
import { LimitsPage } from '@/features/limits/LimitsPage';
import { AdminShell } from '@/components/layout/AdminShell';
import { RequireAdminRole } from '@/features/admin/RequireAdminRole';
import { OpsAlertsPage } from '@/features/admin/OpsAlertsPage';
import { ParamsPage } from '@/features/admin/ParamsPage';
import { MarketVisibilityPage } from '@/features/admin/MarketVisibilityPage';
import { FixtureQuarantinePage } from '@/features/admin/FixtureQuarantinePage';
import { UsersPage } from '@/features/admin/UsersPage';
import { UserDetailPage } from '@/features/admin/UserDetailPage';
import { ReportsPage } from '@/features/admin/ReportsPage';
import { OddsEnginePage } from '@/features/admin/OddsEnginePage';
import { ResettlementPage } from '@/features/admin/ResettlementPage';
import { AuditLogPage } from '@/features/admin/AuditLogPage';
import { LegalPage } from '@/features/legal/LegalPage';
import { LegalIndexPage } from '@/features/legal/LegalIndexPage';
import { tokenStore } from '@/store/auth';

const REFRESH_KEY = 'puntsave_refresh_token';

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!tokenStore.isLoggedIn()) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  /**
   * On mount, attempt to restore the session from a persisted refresh token.
   * Without this, every page reload clears the in-memory access token and
   * RequireAuth immediately redirects to /login even though the user has a
   * valid refresh token in sessionStorage.
   *
   * isInitializing stays true until the restore attempt completes (or we
   * confirm there is no token to restore) so we don't flash /login.
   */
  const [isInitializing, setIsInitializing] = useState(
    () => !tokenStore.isLoggedIn() && !!sessionStorage.getItem(REFRESH_KEY)
  );

  useEffect(() => {
    if (!isInitializing) return;
    tokenStore.refresh().finally(() => setIsInitializing(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login"  element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />

      {/*
        Legal documents. PUBLIC and unauthenticated by design — a customer
        deciding whether to sign up, and a regulator or journalist reading the
        terms, both arrive without an account, so putting these behind
        RequireAuth would hide them from exactly the audience they exist for.
        MUST stay above the catch-all below for the same reason the admin group
        does: a route declared after it never matches, and every /legal URL —
        including the ones printed in the footer of every page — would silently
        redirect to /bets.
      */}
      <Route path="/legal"       element={<LegalIndexPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />

      {/* Protected routes (inside AppShell) */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/bets" replace />} />
        <Route path="/bets"    element={<BetsPage />} />
        {/* sport → match → every market on the match. The id is the fixture
            half of a market id (fixtures.ts); the page is a projection of the
            same /markets payload the board uses. */}
        <Route path="/bets/event/:fixtureId" element={<EventPage />} />
        <Route path="/bets/history" element={<BetHistoryPage />} />
        <Route path="/sports"  element={<SportsPage />} />
        <Route path="/savings" element={<SavingsPage />} />
        <Route path="/deposit" element={<DepositPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/limits"  element={<LimitsPage />} />
      </Route>

      {/*
        Staff console. MUST stay above the catch-all below — a route group
        declared after it never matches, and every /admin URL would silently
        redirect to /bets, which reads as "the console is gone" rather than as
        a routing bug. RequireAdminRole is a DISPLAY gate only; the API
        re-checks the JWT claim on every request and is the authority.
      */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireAdminRole>
              <AdminShell />
            </RequireAdminRole>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/admin/alerts" replace />} />
        <Route path="alerts"         element={<OpsAlertsPage />} />
        <Route path="params"         element={<ParamsPage />} />
        <Route path="markets"        element={<MarketVisibilityPage />} />
        <Route path="quarantines"    element={<FixtureQuarantinePage />} />
        <Route path="users"          element={<UsersPage />} />
        <Route path="users/:userId"  element={<UserDetailPage />} />
        <Route path="resettlement"   element={<ResettlementPage />} />
        <Route path="reports"        element={<ReportsPage />} />
        <Route path="engine"         element={<OddsEnginePage />} />
        <Route path="audit"          element={<AuditLogPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/bets" replace />} />
    </Routes>
  );
}
